import { useEffect, useReducer } from 'react'
import { getLibertyAppApiBase } from '@ambire-common/services/libertySwap/apiBase'

// LibertySwap token icons used to be loaded from
// https://libertyswap.finance/icons/token/<name>.svg — a host that sends no
// `Access-Control-Allow-Origin` header, so the cross-origin probe in
// `checkIfImageExists` (and the <Image> load) is CORS-blocked on every web
// origin (mobile-web at http://localhost:19006, the extension popup, etc.).
//
// The LibertySwap token-list instead exposes a CORS-enabled `logoURI` per
// token. This module fetches that list once, caches it in memory, and maps
// `<chainId>:<address>` → logoURI so the swap TO-list can resolve a loadable
// icon without bundling the (21MB) icon set into the app.
//
// Only icon URLs are taken from the list — the from/to pairing graph stays in
// the app's own constants (`getAvailableToTokens`).

// Two sources, merged with the new list winning:
//  - NEW: standard (Uniswap-style) token list on the LibertySwap CDN (same host
//    as the dapp list). Richer for PulseChain (chain 369) but PulseChain-ONLY —
//    it has no icons for Ethereum/BNB/Base/Arbitrum/etc.
//  - OLD: the supported-tokens API. Fewer PulseChain tokens, but the only source
//    with icons for the non-PulseChain blue-chips (USDC/USDT/DAI/WETH/ETH …).
// We prefer NEW per `chainId:address` and fall back to OLD for keys NEW lacks.
const NEW_TOKENS_URL = 'https://cdn.libertyswap.finance/app-tokens/libertyx-tokenlist.json'
// Follows the dev/prod switch — a dev build must read the dev catalog, which is
// the one that lists newly added tokens (USDG-Robinhood, USDC-Monad …).
const getOldTokensUrl = () => `${getLibertyAppApiBase()}/supported-tokens`

// Legacy icon host — CORS-blocked for `fetch`, but a cross-origin <img> renders
// it fine (display isn't CORS-gated). Used only when the catalog's `logoURI`
// is the generic placeholder but a real named icon exists here.
const LIBERTY_ICON_HOST = 'https://libertyswap.finance/icons/token'
// The catalog returns this placeholder as `logoURI` for tokens whose real logo
// it hasn't ingested — but the named icon (`icon` field) often still exists on
// LIBERTY_ICON_HOST (e.g. WPLS, BEEF, BIDET). Treat it as "no real logo".
const PLACEHOLDER_LOGO_SUFFIX = '/unknown.svg'

interface LibertySupportedToken {
  address: string
  chainId: number
  logoURI?: string
  icon?: string
}

const keyOf = (address: string, chainId: number | bigint): string =>
  `${chainId}:${address.toLowerCase()}`

let cache: Map<string, string> | null = null
let inflight: Promise<Map<string, string>> | null = null

/** Fetch one list and resolve each token to its best icon URL. Accepts both the
 *  standard token-list shape (`{ tokens: [...] }`) and the legacy
 *  supported-tokens API shape (`{ data: [...] }`). */
async function fetchList(url: string): Promise<Map<string, string>> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`LibertySwap token-list HTTP ${res.status} (${url})`)

  const json: { tokens?: LibertySupportedToken[]; data?: LibertySupportedToken[] } =
    await res.json()
  const map = new Map<string, string>()
  for (const token of json.tokens ?? json.data ?? []) {
    const hasRealLogo = !!token.logoURI && !token.logoURI.endsWith(PLACEHOLDER_LOGO_SUFFIX)
    // Prefer the CORS-safe logoURI; when it's the placeholder, fall back to the
    // named icon on the legacy host (still a real logo for many tokens).
    const best = hasRealLogo
      ? token.logoURI
      : token.icon
        ? `${LIBERTY_ICON_HOST}/${token.icon}.svg`
        : undefined
    if (best) map.set(keyOf(token.address, token.chainId), best)
  }
  return map
}

async function load(): Promise<Map<string, string>> {
  // Fetch both in parallel; tolerate either failing so one dead source doesn't
  // wipe out all icons. If both fail, surface the error so the caller can retry.
  const [newList, oldList] = await Promise.allSettled([
    fetchList(NEW_TOKENS_URL),
    fetchList(getOldTokensUrl())
  ])
  if (newList.status === 'rejected' && oldList.status === 'rejected') throw newList.reason

  const merged = new Map<string, string>()
  // Seed with OLD first, then let NEW overwrite — NEW wins per `chainId:address`,
  // OLD fills the keys NEW lacks (the non-PulseChain blue-chips).
  if (oldList.status === 'fulfilled') for (const [k, v] of oldList.value) merged.set(k, v)
  if (newList.status === 'fulfilled') for (const [k, v] of newList.value) merged.set(k, v)
  return merged
}

/**
 * Kick off (or reuse) the one-time catalog fetch. Resolves to the populated
 * map. The in-flight promise is shared so concurrent callers fetch once; on
 * failure the inflight slot is cleared so a later call can retry.
 */
export function ensureLibertyTokenIcons(): Promise<Map<string, string>> {
  if (cache) return Promise.resolve(cache)
  if (!inflight) {
    inflight = load()
      .then((map) => {
        cache = map
        return map
      })
      .catch((err) => {
        inflight = null
        throw err
      })
  }
  return inflight
}

// Native tokens travel under two spellings: the portfolio/quote providers use
// ZeroAddress, the LibertySwap catalog uses 0xEeee… — look the other one up so
// a native leg (PLS, ETH) still resolves its logo.
const NATIVE_ADDRESS_ALIASES = [
  '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
  '0x0000000000000000000000000000000000000000'
]

/** Synchronous lookup against the cached catalog. Returns undefined until the
 *  fetch has resolved or for tokens not in the LibertySwap catalog. */
export function getLibertyLogoURI(address: string, chainId: number | bigint): string | undefined {
  if (!cache) return undefined

  const direct = cache.get(keyOf(address, chainId))
  if (direct) return direct

  const lower = address.toLowerCase()
  if (!NATIVE_ADDRESS_ALIASES.includes(lower)) return undefined

  const alias = NATIVE_ADDRESS_ALIASES.find((a) => a !== lower)!
  return cache.get(keyOf(alias, chainId)) ?? undefined
}

/**
 * React hook: ensures the catalog is fetched, re-renders the caller once it
 * lands, and returns a stable `getLibertyLogoURI` resolver plus a `version`
 * that flips from 0 → 1 when the cache is ready (include it in memo deps that
 * derive icon URLs so they recompute after load).
 */
export function useLibertyTokenIcons(): {
  getLogoURI: typeof getLibertyLogoURI
  version: number
} {
  const [version, bump] = useReducer((x: number) => x + 1, cache ? 1 : 0)

  useEffect(() => {
    if (cache) return
    let active = true
    ensureLibertyTokenIcons()
      .then(() => {
        if (active) bump()
      })
      .catch(() => {
        // Non-fatal: TokenIcon falls back to the cena icon proxy / missing icon.
      })
    return () => {
      active = false
    }
  }, [])

  return { getLogoURI: getLibertyLogoURI, version }
}
